import React, { useState, useEffect, Children } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaCopy, FaDownload, FaCode, FaCss3Alt, FaShieldAlt, FaSpinner, FaArrowLeft, FaCheck, FaInfoCircle, FaExclamationTriangle, FaGlobe, FaList, FaServer, FaDatabase, FaRocket } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { get } from '../../helpers/apiHelper';

// Componente de Tabs para mostrar código en múltiples lenguajes
const CodeTabs = ({ children, defaultTab = 'javascript' }) => {
    const [activeTab, setActiveTab] = useState(defaultTab);
    
    const tabs = Children.toArray(children).filter(child => child && child.props);
    
    return (
        <div className="w-full">
            <div className="flex border-b border-gray-300 mb-4">
                {tabs.map((child, index) => {
                    const tabName = child.props.tab;
                    return (
                        <button
                            key={index}
                            onClick={() => setActiveTab(tabName)}
                            className={`px-4 py-2 font-medium text-sm transition-colors ${
                                activeTab === tabName
                                    ? 'border-b-2 border-blue-600 text-blue-600'
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            {tabName === 'javascript' ? 'JavaScript' : tabName === 'php' ? 'PHP' : tabName}
                        </button>
                    );
                })}
            </div>
            <div>
                {tabs.map((child, index) => {
                    if (child.props.tab === activeTab) {
                        return <div key={index}>{child}</div>;
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

// Componente para cada tab de código
const CodeTab = ({ tab, children }) => {
    return <div>{children}</div>;
};

const CreateFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [marca, setMarca] = useState(null);
    const [tiendas, setTiendas] = useState([]);

    useEffect(() => {
        // Obtener datos de marca y tiendas desde la navegación
        if (location.state?.marca && location.state?.tiendas) {
            setMarca(location.state.marca);
            setTiendas(location.state.tiendas);
            setLoading(false);
        } else {
            // Si no hay datos, cargar desde la API
            fetchData();
        }
    }, [location.state]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [marcasData, tiendasData] = await Promise.all([
                get('marcas'),
                get('tiendas')
            ]);

            if (marcasData.length > 0) {
                setMarca(marcasData[0]);
                setTiendas(tiendasData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudieron cargar los datos. Inténtalo de nuevo.',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            Swal.fire({
                title: 'Copiado!',
                text: 'Código copiado al portapapeles',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error copying to clipboard:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
                    <p className="text-gray-600">Cargando documentación...</p>
                </div>
            </div>
        );
    }

    if (!marca) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <FaExclamationTriangle className="text-6xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">No hay marcas disponibles</h2>
                    <p className="text-gray-600 mb-6">Necesitas crear una marca antes de configurar el formulario</p>
                    <button
                        onClick={() => navigate('/marcas-tiendas')}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <FaArrowLeft className="inline mr-2" />
                        Volver a Marcas
                    </button>
                </div>
            </div>
        );
    }

    const apiUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.petitprime.com/api';
    const endpoint = `${apiUrl}/clientes/embed/register`;

    // Debug: mostrar la URL que se está usando
    console.log('API URL:', apiUrl);
    console.log('Endpoint completo:', endpoint);

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <button
                                onClick={() => navigate('/marcas-tiendas')}
                                className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
                            >
                                <FaArrowLeft className="mr-2" />
                                Volver a Marcas
                            </button>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                                <FaGlobe className="mr-3 text-orange-500" />
                                Crea tu Formulario Propio en tu Web
                            </h1>
                            <p className="text-gray-600 mt-2">
                                Roadmap completo para integrar tu formulario de registro en tu sitio web
                            </p>
                        </div>
                    </div>
                </div>

                {/* Roadmap Completo */}
                <div className="space-y-8">

                    {/* Paso 1: Campos del Formulario */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-blue-100 p-3 rounded-full mr-4">
                                <FaList className="text-blue-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">1. Campos del Formulario</h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Campos Obligatorios */}
                            <div className="bg-red-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                                    <FaCheck className="mr-2" />
                                    Campos Obligatorios
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">nombre</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">apellido</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">email</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">country</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">acceptTerms</span>
                                        <span className="text-sm text-gray-500">boolean</span>
                                    </div>
                                </div>
                            </div>

                            {/* Campos Opcionales */}
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                                    <FaInfoCircle className="mr-2" />
                                    Campos Opcionales
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">telefono</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">fechaCumpleanos</span>
                                        <span className="text-sm text-gray-500">date</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">genero</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="ml-4 text-xs text-gray-600">
                                        Valores: "HOMBRE", "MUJER", "NO_BINARIO", "PREFIERO_NO_DECIRLO"
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">state</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">city</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">postal_code</span>
                                        <span className="text-sm text-gray-500">string</span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                                        <span className="font-medium">acceptTerms</span>
                                        <span className="text-sm text-gray-500">boolean</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Paso 2: Información de Marca y Tiendas */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-green-100 p-3 rounded-full mr-4">
                                <FaDatabase className="text-green-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">2. Información de tu Cuenta</h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Marca */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-blue-800 mb-3">
                                    🏢 Tu Marca
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            ID de Marca
                                        </label>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                value={marca?._id || 'Cargando...'}
                                                readOnly
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                            />
                                            <button
                                                onClick={() => copyToClipboard(marca?._id)}
                                                className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                            >
                                                <FaCopy />
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nombre de Marca
                                        </label>
                                        <input
                                            type="text"
                                            value={marca?.nombre || 'Cargando...'}
                                            readOnly
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Tiendas */}
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-green-800 mb-3">
                                    🏪 Tus Tiendas
                                </h3>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {tiendas.map((tienda, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                                            <div>
                                                <div className="font-medium">{tienda.nombre}</div>
                                                <div className="text-sm text-gray-500">{tienda.poblacion}</div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                    ID: {tienda._id}
                                                </span>
                                                <button
                                                    onClick={() => copyToClipboard(tienda._id)}
                                                    className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                                >
                                                    <FaCopy />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Paso 3: Endpoint */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-purple-100 p-3 rounded-full mr-4">
                                <FaServer className="text-purple-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">3. Endpoint de Registro</h2>
                        </div>

                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-yellow-400">POST</span>
                                <button
                                    onClick={() => copyToClipboard(endpoint)}
                                    className="px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                >
                                    <FaCopy className="inline mr-1" />
                                    Copiar
                                </button>
                            </div>
                            <div className="text-white">{endpoint}</div>
                        </div>

                        <div className="mt-4 bg-yellow-50 p-4 rounded-lg">
                            <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                                ⚠️ Información Importante
                            </h4>
                            <ul className="text-sm text-yellow-700 space-y-1">
                                <li>• Método: <strong>POST</strong></li>
                                <li>• Content-Type: <strong>application/json</strong></li>
                                <li>• El campo <strong>marca</strong> es obligatorio (usa tu ID de marca)</li>
                                <li>• El campo <strong>tienda</strong> es opcional (usa el ID de tienda específica)</li>
                                <li>• El campo <strong>website</strong> (honeypot) es <strong className="text-red-600">OBLIGATORIO</strong> - siempre debe estar presente y vacío</li>
                                <li>• El campo <strong>challengeToken</strong> es <strong className="text-red-600">OBLIGATORIO</strong> - sin él, todas las peticiones serán rechazadas (ver paso 8)</li>
                                <li>• Todos los demás campos opcionales pueden omitirse</li>
                            </ul>
                        </div>
                    </div>

                    {/* Paso 4: Estructura de Datos */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-orange-100 p-3 rounded-full mr-4">
                                <FaCode className="text-orange-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">4. Estructura de Datos</h2>
                        </div>

                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                            <pre>{`{
  "nombre": "Juan",
  "apellido": "Pérez", 
  "email": "juan.perez@example.com",
  "telefono": "+34123456789",
  "fechaCumpleanos": "1990-05-15T00:00:00.000Z",
  "genero": "HOMBRE",
  "geographic_info": {
    "country": "España",
    "state": "Madrid", 
    "city": "Madrid",
    "postal_code": "28001"
  },
  "marca": "${marca?._id}",
  "tienda": "${tiendas[0]?._id || ''}",
  "acceptTerms": true,
  "acceptPrivacy": true,
  "acceptMarketing": false,
  "website": "",
  "challengeToken": "jwt_token_del_challenge"
}`}</pre>
                        </div>

                        <div className="mt-4 flex space-x-2">
                            <button
                                onClick={() => copyToClipboard(JSON.stringify({
                                    nombre: "Juan",
                                    apellido: "Pérez",
                                    email: "juan.perez@example.com",
                                    telefono: "+34123456789",
                                    fechaCumpleanos: "1990-05-15T00:00:00.000Z",
                                    genero: "HOMBRE",
                                    geographic_info: {
                                        country: "España",
                                        state: "Madrid",
                                        city: "Madrid",
                                        postal_code: "28001"
                                    },
                                    marca: marca?._id,
                                    tienda: tiendas[0]?._id || '',
                                    acceptTerms: true,
                                    acceptPrivacy: true,
                                    acceptMarketing: false,
                                    website: "",
                                    challengeToken: "jwt_token_del_challenge"
                                }, null, 2))}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                            >
                                <FaCopy className="inline mr-2" />
                                Copiar Ejemplo
                            </button>
                        </div>
                    </div>

                    {/* Paso 5: Respuestas de la API */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-green-100 p-3 rounded-full mr-4">
                                <FaRocket className="text-green-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">5. Respuestas de la API</h2>
                        </div>

                        <div className="space-y-4">
                            {/* Éxito */}
                            <div>
                                <h4 className="text-lg font-semibold text-green-800 mb-2">✅ Registro Exitoso (201)</h4>
                                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                                    <pre>{`{
  "success": true,
  "data": {
    "id": "64a1b2c3d4e5f6789012345",
    "nombre": "Juan",
    "apellido": "Pérez", 
    "email": "juan.perez@example.com",
    "codigoReferido": "JUA123456"
  },
  "message": "Cliente registrado exitosamente"
}`}</pre>
                                </div>
                            </div>

                            {/* Error de Validación */}
                            <div>
                                <h4 className="text-lg font-semibold text-red-800 mb-2">❌ Error de Validación (400)</h4>
                                <div className="bg-gray-900 text-red-400 p-4 rounded-lg font-mono text-sm">
                                    <pre>{`{
  "success": false,
  "error": "Debe aceptar los términos y condiciones"
}`}</pre>
                                </div>
                            </div>

                            {/* Cliente Ya Existe */}
                            <div>
                                <h4 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ Cliente Ya Existe (403)</h4>
                                <div className="bg-gray-900 text-yellow-400 p-4 rounded-lg font-mono text-sm">
                                    <pre>{`{
  "success": false,
  "error": "Ya existe un cliente con este email para esta tienda"
}`}</pre>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Paso 6: Ejemplo de Implementación */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-indigo-100 p-3 rounded-full mr-4">
                                <FaCss3Alt className="text-indigo-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">6. Ejemplo de Implementación</h2>
                        </div>

                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                            <pre>{`// JavaScript para enviar datos con seguridad
let challengeToken = null;

// Escuchar mensajes del iframe del challenge
window.addEventListener('message', (event) => {
    if (event.origin !== 'https://petitprime.com') return;

    if (event.data.type === 'challenge:success') {
        challengeToken = event.data.challengeToken;
        console.log('✅ Challenge token obtenido');
    } else if (event.data.type === 'challenge:error') {
        console.error('❌ Error:', event.data.error);
        challengeToken = null;
    }
});

async function registerClient(formData) {
    // Validar Challenge Token
    if (!challengeToken) {
        alert('Por favor, completa la verificación de seguridad primero');
        return;
    }

    try {
        const response = await fetch('${endpoint}?code=TU_CODE', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...formData,
                website: '',  // OBLIGATORIO: honeypot vacío
                challengeToken: challengeToken  // ← OBLIGATORIO: JWT del challenge
            })
        });

        const result = await response.json();
        
        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        throw error;
    }
}

// Ejemplo de uso
const formData = {
    nombre: document.getElementById('nombre').value,
    apellido: document.getElementById('apellido').value,
    email: document.getElementById('email').value,
    marca: '${marca?._id}',
    tienda: '${tiendas[0]?._id || ''}',
    acceptTerms: document.getElementById('acceptTerms').checked
};

try {
    const result = await registerClient(formData);
    console.log('Registro exitoso:', result);
} catch (error) {
    console.error('Error:', error.message);
}`}</pre>
                        </div>

                        <div className="mt-4 flex space-x-2">
                            <button
                                onClick={() => copyToClipboard(`let challengeToken = null;

window.addEventListener('message', (event) => {
    if (event.origin !== 'https://petitprime.com') return;

    if (event.data.type === 'challenge:success') {
        challengeToken = event.data.challengeToken;
    } else if (event.data.type === 'challenge:error') {
        challengeToken = null;
    }
});

async function registerClient(formData) {
    if (!challengeToken) {
        alert('Por favor, completa la verificación de seguridad primero');
        return;
    }

    try {
        const response = await fetch('${endpoint}?code=TU_CODE', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...formData,
                website: '',
                challengeToken: challengeToken
            })
        });

        const result = await response.json();
        
        if (result.success) {
            return result.data;
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        throw error;
    }
}`)}
                                className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 text-sm"
                            >
                                <FaCopy className="inline mr-2" />
                                Copiar Código
                            </button>
                        </div>
                    </div>

                    {/* Valores Posibles de Campos */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center mb-4">
                            <div className="bg-purple-100 p-3 rounded-full mr-4">
                                <FaInfoCircle className="text-purple-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">7. Valores Posibles de Campos</h2>
                        </div>

                        <div className="space-y-4">
                            {/* Género */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Género (genero)</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <div className="bg-white p-2 rounded border text-center">
                                        <code className="text-sm">"HOMBRE"</code>
                                    </div>
                                    <div className="bg-white p-2 rounded border text-center">
                                        <code className="text-sm">"MUJER"</code>
                                    </div>
                                    <div className="bg-white p-2 rounded border text-center">
                                        <code className="text-sm">"NO_BINARIO"</code>
                                    </div>
                                    <div className="bg-white p-2 rounded border text-center">
                                        <code className="text-sm">"PREFIERO_NO_DECIRLO"</code>
                                    </div>
                                </div>
                            </div>

                            {/* Fecha de Cumpleaños */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Fecha de Cumpleaños (fechaCumpleanos)</h4>
                                <div className="bg-white p-3 rounded border">
                                    <code className="text-sm">Formato ISO 8601: "1990-05-15T00:00:00.000Z"</code>
                                </div>
                            </div>

                            {/* Geographic Info */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Información Geográfica (geographic_info)</h4>
                                <div className="bg-white p-3 rounded border">
                                    <pre className="text-sm">{`{
  "country": "España",
  "state": "Madrid",
  "city": "Madrid", 
  "postal_code": "28001"
}`}</pre>
                                </div>
                            </div>

                            {/* Campos Booleanos */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Campos Booleanos (Obligatorios)</h4>
                                <div className="bg-white p-3 rounded border">
                                    <p className="text-sm text-gray-700 mb-2">
                                        <strong>Importante:</strong> Este campo es obligatorio para el endpoint embed/register.
                                    </p>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                            <code className="text-sm">acceptTerms: true</code>
                                            <span className="text-xs text-gray-500">Obligatorio</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Código de Referido - COMENTADO: No implementado aún */}
                            {/* 
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Código de Referido (referidoPor)</h4>
                                <div className="bg-white p-3 rounded border">
                                    <p className="text-sm text-gray-700 mb-2">
                                        <strong>Importante:</strong> No se envía el código directamente, sino el ID del cliente referido.
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Primero valida el código con: <code>GET /api/clientes/referral?code=CODIGO&marcaId=MARCA_ID</code>
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Luego envía el ID del cliente: <code>"referidoPor": "ID_DEL_CLIENTE"</code>
                                    </p>
                                </div>
                            </div>
                            */}
                        </div>
                    </div>

                    {/* Paso 8: Requisitos de Seguridad */}
                    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
                        <div className="flex items-center mb-4">
                            <div className="bg-red-100 p-3 rounded-full mr-4">
                                <FaShieldAlt className="text-red-600 text-xl" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">8. Requisitos de Seguridad ⚠️ OBLIGATORIO</h2>
                        </div>

                        <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
                            <div className="flex items-start">
                                <FaExclamationTriangle className="text-red-600 text-2xl mr-3 mt-1" />
                                <div>
                                    <h3 className="text-lg font-semibold text-red-800 mb-2">
                                        Importante: Medidas de Seguridad Obligatorias
                                    </h3>
                                    <p className="text-sm text-red-700 mb-3">
                                        Para proteger nuestro sistema y garantizar el correcto funcionamiento del endpoint, 
                                        <strong> DEBES implementar las siguientes medidas de seguridad:</strong>
                                    </p>
                                    <ul className="text-sm text-red-700 space-y-2 list-disc list-inside">
                                        <li><strong>Campo Honeypot (website)</strong> (OBLIGATORIO - debe estar presente en todas las peticiones)</li>
                                        <li><strong>Challenge Token</strong> (OBLIGATORIO - método que NO requiere registrar hostnames en Cloudflare)</li>
                                        <li><strong>Validación de datos</strong> antes de enviar</li>
                                    </ul>
                                    <p className="text-sm text-red-800 font-semibold mt-3">
                                        ⚠️ Sin el campo honeypot o sin challenge token válido, todas las peticiones serán rechazadas con error 400.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Campo Honeypot */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                                <FaShieldAlt className="mr-2 text-red-600" />
                                1. Campo Honeypot (OBLIGATORIO)
                            </h3>
                            
                            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                                <p className="text-sm text-red-800 mb-3">
                                    <strong>⚠️ OBLIGATORIO:</strong> El campo <code>website</code> debe estar presente en todas las peticiones. 
                                    Sin él, todas las peticiones serán rechazadas con error 400.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">HTML:</h4>
                                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                    <pre>{`<!-- Campo Honeypot (OCULTO) -->
<input 
    type="text" 
    name="website" 
    style="display: none !important; visibility: hidden; position: absolute; left: -9999px;"
    tabindex="-1"
    autocomplete="off"
    aria-hidden="true"
/>`}</pre>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(`<input 
    type="text" 
    name="website" 
    style="display: none !important; visibility: hidden; position: absolute; left: -9999px;"
    tabindex="-1"
    autocomplete="off"
    aria-hidden="true"
/>`)}
                                    className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                >
                                    <FaCopy className="inline mr-1" />
                                    Copiar Código
                                </button>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-semibold text-gray-800 mb-2">JavaScript (Incluir en POST):</h4>
                                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                    <pre>{`const formData = {
    nombre: document.getElementById('nombre').value,
    apellido: document.getElementById('apellido').value,
    email: document.getElementById('email').value,
    marca: '${marca?._id}',
    tienda: '${tiendas[0]?._id || ''}',
    acceptTerms: true,
    website: '',  // ← OBLIGATORIO: siempre vacío
    challengeToken: challengeToken  // Ver siguiente sección
};`}</pre>
                                </div>
                            </div>
                        </div>

                        {/* Challenge Token */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                                <FaShieldAlt className="mr-2 text-red-600" />
                                2. Challenge Token (OBLIGATORIO)
                            </h3>
                            
                            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                                <p className="text-sm text-red-800 mb-2">
                                    <strong>⚠️ OBLIGATORIO:</strong> El Challenge Token es OBLIGATORIO para todas las peticiones. 
                                    Sin un challenge token válido, todas las peticiones serán rechazadas con error 400.
                                </p>
                                <p className="text-sm text-red-700 mb-2">
                                    Este método NO requiere registrar hostnames de tus clientes en Cloudflare. 
                                    El challenge se ejecuta desde <code>api.petitprime.com</code>.
                                </p>
                                <div className="bg-white p-3 rounded border border-red-300 mt-3">
                                    <p className="text-sm text-red-800 font-semibold mb-2">Error si falta challenge token:</p>
                                    <pre className="text-xs bg-gray-900 text-red-400 p-2 rounded overflow-x-auto">{`{
  "success": false,
  "error": "Challenge token requerido",
  "details": {
    "message": "El endpoint requiere un challenge token válido...",
    "challengeUrl": "/api/clientes/embed/challenge"
  }
}`}</pre>
                                </div>
                            </div>
                            
                            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                                <p className="text-sm text-blue-800 mb-2 font-semibold">Ventajas del Challenge Token:</p>
                                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                                    <li>No requiere registrar hostnames de clientes</li>
                                    <li>Escalable - Funciona con cualquier número de clientes</li>
                                    <li>Seguro - JWT corto (2 minutos) con validación</li>
                                    <li>Simple - Solo necesitas cargar un iframe</li>
                                </ul>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Paso 1: Añadir Iframe del Challenge</h4>
                                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                    <pre>{`<!-- Iframe del challenge -->
<iframe 
    id="challengeFrame"
    src="https://api.petitprime.com/api/clientes/embed/challenge"
    style="width: 100%; height: 200px; border: 1px solid #ddd; border-radius: 4px;"
    sandbox="allow-scripts allow-same-origin"
></iframe>`}</pre>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(`<iframe 
    id="challengeFrame"
    src="https://api.petitprime.com/api/clientes/embed/challenge"
    style="width: 100%; height: 200px; border: 1px solid #ddd; border-radius: 4px;"
    sandbox="allow-scripts allow-same-origin"
></iframe>`)}
                                    className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                >
                                    <FaCopy className="inline mr-1" />
                                    Copiar Código
                                </button>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Paso 2: Escuchar Mensajes del Iframe</h4>
                                <CodeTabs defaultTab="javascript">
                                    <CodeTab tab="javascript">
                                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                            <pre>{`let challengeToken = null;

// Escuchar mensajes del iframe
window.addEventListener('message', (event) => {
    if (event.origin !== 'https://api.petitprime.com') return;

    if (event.data.type === 'challenge:success') {
        challengeToken = event.data.challengeToken;
        console.log('✅ Challenge token obtenido');
    } else if (event.data.type === 'challenge:error') {
        console.error('❌ Error:', event.data.error);
    }
});`}</pre>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(`let challengeToken = null;

window.addEventListener('message', (event) => {
    if (event.origin !== 'https://api.petitprime.com') return;

    if (event.data.type === 'challenge:success') {
        challengeToken = event.data.challengeToken;
        console.log('✅ Challenge token obtenido');
    } else if (event.data.type === 'challenge:error') {
        console.error('❌ Error:', event.data.error);
    }
});`)}
                                            className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                        >
                                            <FaCopy className="inline mr-1" />
                                            Copiar Código
                                        </button>
                                    </CodeTab>
                                    <CodeTab tab="php">
                                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                            <pre>{`<?php
// En el frontend (JavaScript embebido en PHP)
?>
<script>
let challengeToken = null;

// Escuchar mensajes del iframe
window.addEventListener('message', (event) => {
    if (event.origin !== 'https://api.petitprime.com') return;

    if (event.data.type === 'challenge:success') {
        challengeToken = event.data.challengeToken;
        console.log('✅ Challenge token obtenido');
    } else if (event.data.type === 'challenge:error') {
        console.error('❌ Error:', event.data.error);
    }
});
</script>`}</pre>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(`<?php
// En el frontend (JavaScript embebido en PHP)
?>
<script>
let challengeToken = null;

window.addEventListener('message', (event) => {
    if (event.origin !== 'https://api.petitprime.com') return;

    if (event.data.type === 'challenge:success') {
        challengeToken = event.data.challengeToken;
        console.log('✅ Challenge token obtenido');
    } else if (event.data.type === 'challenge:error') {
        console.error('❌ Error:', event.data.error);
    }
});
</script>`)}
                                            className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                        >
                                            <FaCopy className="inline mr-1" />
                                            Copiar Código
                                        </button>
                                    </CodeTab>
                                </CodeTabs>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-semibold text-gray-800 mb-2">Paso 3: Incluir Challenge Token en el POST</h4>
                                <CodeTabs defaultTab="javascript">
                                    <CodeTab tab="javascript">
                                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                            <pre>{`const formData = {
    nombre: document.getElementById('nombre').value,
    apellido: document.getElementById('apellido').value,
    email: document.getElementById('email').value,
    marca: '${marca?._id}',
    tienda: '${tiendas[0]?._id || ''}',
    acceptTerms: true,
    website: '',  // OBLIGATORIO
    challengeToken: challengeToken  // ← JWT del challenge
};

const response = await fetch('${endpoint}?code=TU_CODE', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
});

const result = await response.json();
if (result.success) {
    console.log('✅ Cliente registrado:', result.data);
} else {
    console.error('❌ Error:', result.error);
}`}</pre>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(`const formData = {
    nombre: document.getElementById('nombre').value,
    apellido: document.getElementById('apellido').value,
    email: document.getElementById('email').value,
    marca: '${marca?._id}',
    tienda: '${tiendas[0]?._id || ''}',
    acceptTerms: true,
    website: '',  // OBLIGATORIO
    challengeToken: challengeToken  // ← JWT del challenge
};

const response = await fetch('${endpoint}?code=TU_CODE', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json'
    },
    body: JSON.stringify(formData)
});

const result = await response.json();
if (result.success) {
    console.log('✅ Cliente registrado:', result.data);
} else {
    console.error('❌ Error:', result.error);
}`)}
                                            className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                        >
                                            <FaCopy className="inline mr-1" />
                                            Copiar Código
                                        </button>
                                    </CodeTab>
                                    <CodeTab tab="php">
                                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                            <pre>{`<?php
// Obtener datos del formulario
$nombre = $_POST['nombre'] ?? '';
$apellido = $_POST['apellido'] ?? '';
$email = $_POST['email'] ?? '';
$challengeToken = $_POST['challengeToken'] ?? '';

// Preparar datos
$formData = [
    'nombre' => $nombre,
    'apellido' => $apellido,
    'email' => $email,
    'marca' => '${marca?._id}',
    'tienda' => '${tiendas[0]?._id || ''}',
    'acceptTerms' => true,
    'website' => '',  // OBLIGATORIO
    'challengeToken' => $challengeToken  // ← JWT del challenge
];

// Enviar petición
$ch = curl_init('${endpoint}?code=TU_CODE');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($formData));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json'
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$result = json_decode($response, true);

if ($result['success'] ?? false) {
    echo '✅ Cliente registrado: ' . json_encode($result['data']);
} else {
    echo '❌ Error: ' . ($result['error'] ?? 'Error desconocido');
}
?>`}</pre>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(`<?php
// Obtener datos del formulario
$nombre = $_POST['nombre'] ?? '';
$apellido = $_POST['apellido'] ?? '';
$email = $_POST['email'] ?? '';
$challengeToken = $_POST['challengeToken'] ?? '';

// Preparar datos
$formData = [
    'nombre' => $nombre,
    'apellido' => $apellido,
    'email' => $email,
    'marca' => '${marca?._id}',
    'tienda' => '${tiendas[0]?._id || ''}',
    'acceptTerms' => true,
    'website' => '',  // OBLIGATORIO
    'challengeToken' => $challengeToken  // ← JWT del challenge
];

// Enviar petición
$ch = curl_init('${endpoint}?code=TU_CODE');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($formData));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json'
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$result = json_decode($response, true);

if ($result['success'] ?? false) {
    echo '✅ Cliente registrado: ' . json_encode($result['data']);
} else {
    echo '❌ Error: ' . ($result['error'] ?? 'Error desconocido');
}
?>`)}
                                            className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                        >
                                            <FaCopy className="inline mr-1" />
                                            Copiar Código
                                        </button>
                                    </CodeTab>
                                </CodeTabs>
                            </div>
                        </div>

                        {/* Ejemplo Completo con Seguridad */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                                <FaCode className="mr-2 text-purple-600" />
                                3. Ejemplo Completo con Seguridad
                            </h3>
                            
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                                    <pre>{`<!DOCTYPE html>
<html>
<head>
    <title>Formulario de Registro</title>
</head>
<body>
    <form id="registrationForm">
        <!-- Campos del formulario -->
        <input type="text" id="nombre" name="nombre" required>
        <input type="text" id="apellido" name="apellido" required>
        <input type="email" id="email" name="email" required>
        
        <!-- Campo Honeypot (OCULTO - OBLIGATORIO) -->
        <input 
            type="text" 
            name="website" 
            style="display: none !important; visibility: hidden; position: absolute; left: -9999px;"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
        />
        
        <!-- Iframe del Challenge -->
        <iframe 
            id="challengeFrame"
            src="https://api.petitprime.com/api/clientes/embed/challenge"
            style="width: 100%; height: 200px; border: 1px solid #ddd; border-radius: 4px;"
            sandbox="allow-scripts allow-same-origin"
        ></iframe>
        
        <button type="submit">Registrarse</button>
    </form>

    <script>
        let challengeToken = null;

        // Escuchar mensajes del iframe
        window.addEventListener('message', (event) => {
            if (event.origin !== 'https://api.petitprime.com') return;

            if (event.data.type === 'challenge:success') {
                challengeToken = event.data.challengeToken;
                console.log('✅ Challenge token obtenido');
                document.getElementById('challengeFrame').style.borderColor = '#28a745';
            } else if (event.data.type === 'challenge:error') {
                console.error('❌ Error:', event.data.error);
                challengeToken = null;
            }
        });

        document.getElementById('registrationForm').addEventListener('submit', async (e) => {
            e.preventDefault();
            
            // Validar honeypot (OBLIGATORIO)
            const honeypot = document.querySelector('[name="website"]').value;
            if (honeypot && honeypot.trim() !== '') {
                console.warn('Bot detectado por honeypot');
                return;
            }
            
            // Validar Challenge Token (OBLIGATORIO)
            if (!challengeToken) {
                alert('Por favor, completa la verificación de seguridad primero. El challenge token es obligatorio.');
                return;
            }

            const formData = {
                nombre: document.getElementById('nombre').value,
                apellido: document.getElementById('apellido').value,
                email: document.getElementById('email').value,
                marca: '${marca?._id}',
                tienda: '${tiendas[0]?._id || ''}',
                acceptTerms: true,
                website: '',  // OBLIGATORIO: siempre vacío
                challengeToken: challengeToken  // JWT del challenge
            };

            try {
                const response = await fetch('${endpoint}?code=TU_CODE', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                });

                const result = await response.json();
                
                if (result.success) {
                    alert('¡Registro exitoso!');
                    // Resetear challenge
                    challengeToken = null;
                    document.getElementById('challengeFrame').src = document.getElementById('challengeFrame').src;
                } else {
                    alert('Error: ' + result.error);
                }
            } catch (error) {
                console.error('Error:', error);
                alert('Error de conexión');
            }
        });
    </script>
</body>
</html>`}</pre>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(`<!DOCTYPE html>
<html>
<head>
    <title>Formulario de Registro</title>
</head>
<body>
    <form id="registrationForm">
        <input type="text" id="nombre" name="nombre" required>
        <input type="text" id="apellido" name="apellido" required>
        <input type="email" id="email" name="email" required>
        
        <input 
            type="text" 
            name="website" 
            style="display: none !important; visibility: hidden; position: absolute; left: -9999px;"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
        />
        
        <iframe 
            id="challengeFrame"
            src="https://api.petitprime.com/api/clientes/embed/challenge"
            style="width: 100%; height: 200px; border: 1px solid #ddd; border-radius: 4px;"
            sandbox="allow-scripts allow-same-origin"
        ></iframe>
        
        <button type="submit">Registrarse</button>
    </form>

    <script>
        let challengeToken = null;

        window.addEventListener('message', (event) => {
            if (event.origin !== 'https://api.petitprime.com') return;

            if (event.data.type === 'challenge:success') {
                challengeToken = event.data.challengeToken;
                document.getElementById('challengeFrame').style.borderColor = '#28a745';
            }
        });

        document.getElementById('registrationForm').addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const honeypot = document.querySelector('[name="website"]').value;
            if (honeypot && honeypot.trim() !== '') {
                return;
            }
            
            if (!challengeToken) {
                alert('Por favor, completa la verificación de seguridad primero');
                return;
            }

            const formData = {
                nombre: document.getElementById('nombre').value,
                apellido: document.getElementById('apellido').value,
                email: document.getElementById('email').value,
                marca: '${marca?._id}',
                tienda: '${tiendas[0]?._id || ''}',
                acceptTerms: true,
                website: '',
                challengeToken: challengeToken
            };

            const response = await fetch('${endpoint}?code=TU_CODE', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const result = await response.json();
            if (result.success) {
                alert('¡Registro exitoso!');
                challengeToken = null;
                document.getElementById('challengeFrame').src = document.getElementById('challengeFrame').src;
            } else {
                alert('Error: ' + result.error);
            }
        });
    </script>
</body>
</html>`)}
                                    className="mt-2 px-3 py-1 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
                                >
                                    <FaCopy className="inline mr-1" />
                                    Copiar Ejemplo Completo
                                </button>
                            </div>
                        </div>

                        {/* Información Adicional de Seguridad */}
                        <div className="mt-6 bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                                <FaInfoCircle className="mr-2" />
                                Información Adicional
                            </h4>
                            <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                                <li>Si usas el <strong>iframe</strong> de PetitPrime, Challenge Token ya está implementado automáticamente.</li>
                                <li>El <strong>Challenge Token</strong> es OBLIGATORIO - sin él, todas las peticiones serán rechazadas.</li>
                                <li>El <strong>campo honeypot (website)</strong> es OBLIGATORIO - debe estar presente y vacío en todas las peticiones.</li>
                                <li>El método Turnstile Directo ya NO está disponible - todos deben usar Challenge Token.</li>
                                <li>Sin estas medidas, tu IP puede ser bloqueada por actividad sospechosa.</li>
                            </ul>
                        </div>
                    </div>

                    {/* Información Adicional */}
                    <div className="bg-blue-50 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold text-blue-800 mb-3">
                            📋 Información Adicional
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <strong>Endpoint:</strong> <code className="bg-gray-200 px-2 py-1 rounded">POST {endpoint}</code>
                            </div>
                            <div>
                                <strong>Marca ID:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{marca?._id}</code>
                            </div>
                            <div>
                                <strong>Tienda ID:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{tiendas[0]?._id || 'Opcional'}</code>
                            </div>
                            <div>
                                <strong>Método:</strong> <code className="bg-gray-200 px-2 py-1 rounded">POST</code>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateFormPage;